import { useAuthHeader } from "react-auth-kit";
import { Price } from "../models/price";
import { refreshSession } from "../../auth/authBridge";

const apiUrl =
  process.env.REACT_APP_ENV === "LOCAL"
    ? "http://localhost:5000"
    : "https://api.spotprisprognos.se:8443";

export interface PricingPlan {
  id: string;
  name: string;
  description: string | null;
  displayOrder: number;
  priceSekPerMonth: number | null;
}

export async function fetchPricing(): Promise<{ plans: PricingPlan[] }> {
  const res = await fetch(`${apiUrl}/api/pricing`);
  if (!res.ok) throw new Error(`Pricing fetch failed (${res.status})`);
  return await res.json();
}

// SEK value of one EUR/NOK/DKK (ECB reference rates), for currency display.
export async function fetchFxRates(): Promise<{ date: string; sekPer: Record<string, number> }> {
  const res = await fetch(`${apiUrl}/api/prices/fx`);
  if (!res.ok) throw new Error(`FX fetch failed (${res.status})`);
  return await res.json();
}

// Once the server reports the caller's permissions changed (stale JWT after a
// plan/role update), every authenticated request will 403. Latch that state so
// all subsequent requests short-circuit immediately instead of looping — this
// kills the redirect loop until the user re-logs in (full page reload) via the
// modal that listens for the "permissions-changed" event below.
let permissionsInvalidated = false;

// A source article behind a signal / summary — the document popup shows exactly
// these fields (title, source, summary, link to original).
export interface SignalSource {
  title: string | null;
  url: string | null;
  summary: string | null;
  source: string | null;
}

export interface MarketSignal {
  summarySv: string;
  summaryEn: string;
  polarity: number;
  magnitude: number;
  eventTime: string | null;
  locationName: string | null;
  lat: number | null;
  lon: number | null;
  sources: SignalSource[];
  createdAt: string;
}

export interface SignalsResponse {
  signals: MarketSignal[];
}

export interface DailySummary {
  summaryDate: string;
  textSv: string;
  textEn: string;
  createdAt: string;
}

export interface SignalSummaryResponse {
  summary: DailySummary | null;
  sources: SignalSource[];
}

interface PricesResponse {
  prices: Price[];
}

// ENTSO-E numeric grid series (generation by fuel, load, forecasts, reservoir).
export interface GridSeries {
  fuel: string; // '' for non-fuel metrics (load, reservoir…)
  unit: string;
  isForecast: boolean;
  points: { t: string; v: number }[];
}

export interface GridSeriesResponse {
  zone: string;
  metric: string;
  series: GridSeries[];
}

// Create a hook to get the auth header
export const useApi = () => {
  const authHeader = useAuthHeader();

  return {
    // Electricity methods
    async fetchElectricityPrices(
      startTime: string,
      endTime: string,
      area: string,
    ): Promise<PricesResponse> {
      const areaParam = area ? `&area=${area}` : "";
      return await this.fetchWithAuth(
        `/prices/electricity?startTime=${startTime}&endTime=${endTime}${areaParam}`,
      );
    },

    async fetchElectricityPredictions(
      area?: string,
      generatedAt?: string,
      range?: "7d" | "30d",
      model?: string | null,
    ): Promise<any> {
      const params = new URLSearchParams();
      if (model) params.set("model", model);
      if (area) params.set("area", area);
      if (generatedAt) params.set("generatedAt", generatedAt);
      if (range) params.set("range", range);
      const qs = params.toString();
      return await this.fetchWithAuth(
        `/predictions/electricity${qs ? `?${qs}` : ""}`,
      );
    },

    async fetchElectricityTimestamps(
      area?: string,
      model?: string | null,
    ): Promise<{ timestamps: string[] }> {
      const params = new URLSearchParams();
      if (area) params.set("area", area);
      if (model) params.set("model", model);
      const qs = params.toString();
      return await this.fetchWithAuth(
        `/predictions/electricity/timestamps${qs ? `?${qs}` : ""}`,
      );
    },

    async fetchGridSeries(
      zone: string,
      metric: string,
      from?: string,
      to?: string,
    ): Promise<GridSeriesResponse> {
      const params = new URLSearchParams({ zone, metric });
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      return await this.fetchWithAuth(`/grid/series?${params.toString()}`);
    },

    async fetchCurrentAccount(): Promise<{
      account: {
        id: string;
        email: string;
        username?: string;
        image_link?: string;
      };
    }> {
      return await this.fetchWithAuth(`/accounts/me`);
    },

    async fetchAccount(accountId: string): Promise<{
      account: {
        id: string;
        email: string;
        username?: string;
        image_link?: string;
      };
    }> {
      return await this.fetchWithAuth(`/accounts/${accountId}`);
    },

    async fetchAllAccounts(): Promise<{
      accounts: Array<{
        id: string;
        email: string;
        username?: string;
        image_link?: string;
      }>;
    }> {
      return await this.fetchWithAuth(`/accounts`);
    },

    async searchAccounts(query: string): Promise<{
      accounts: Array<{
        id: string;
        email: string;
        username?: string;
        image_link?: string;
      }>;
    }> {
      return await this.fetchWithAuthPost(`/accounts/search`, { query });
    },

    async updateAccount(data: {
      username?: string;
      image_link?: string;
    }): Promise<{
      account: {
        id: string;
        email: string;
        username?: string;
        image_link?: string;
      };
    }> {
      return await this.fetchWithAuthPost(`/accounts/update`, data);
    },

    async deleteMyAccount(): Promise<{ message: string }> {
      return await this.fetchWithAuthDelete(`/accounts/me`);
    },

    async fetchPredictionById(predictionId: string): Promise<{
      prediction: {
        id: string;
        type: "electricity";
        targetTime: string;
        predictedValue: number;
        predictionTarget: string;
        reasoning: string | null;
        generatedAt: string;
        userId: string | null;
        area?: string;
      };
    }> {
      return await this.fetchWithAuth(`/predictions/${predictionId}`);
    },

    async train(area: string): Promise<any> {
      return await this.fetchWithAuth(`/training?area=${area}`);
    },

    // Latest spot price per bidding zone (SE1–SE4) for the map choropleth.
    async fetchZonePrices(): Promise<{
      zones: Array<{ area: string; timestamp: string; value: number }>;
    }> {
      return await this.fetchWithAuth(`/prices/electricity/zones`);
    },

    // Hourly actual prices per zone over the past `days`, then the latest forecast.
    async fetchZoneTimeline(days = 3): Promise<{
      areas: string[];
      series: Record<string, Array<{ t: string; v: number; forecast: boolean }>>;
    }> {
      return await this.fetchWithAuth(`/prices/electricity/zones/timeline?days=${days}`);
    },

    // News signals (electricity event summaries) + daily recap.
    async fetchSignals(limit: number = 20): Promise<SignalsResponse> {
      return await this.fetchWithAuth(`/signals?limit=${limit}`);
    },

    async fetchSignalSummary(): Promise<SignalSummaryResponse> {
      return await this.fetchWithAuth(`/signals/summary`);
    },

    // Admin methods
    async fetchAdminUsers(): Promise<{
      users: Array<{
        id: string;
        email: string;
        username?: string;
        image_link?: string;
        roles: Array<{ id: string; name: string }>;
      }>;
    }> {
      return await this.fetchWithAuth(`/admin/users`);
    },

    async fetchAdminRoles(): Promise<{
      roles: Array<{
        id: string;
        name: string;
        permissions: Array<{
          id: string;
          resource: string;
          action: string;
          description?: string;
        }>;
      }>;
    }> {
      return await this.fetchWithAuth(`/admin/roles`);
    },

    async fetchAdminPermissions(): Promise<{
      permissions: Array<{
        id: string;
        resource: string;
        action: string;
        description?: string;
      }>;
    }> {
      return await this.fetchWithAuth(`/admin/permissions`);
    },

    async fetchAdminTelemetry(days = 30): Promise<{
      dau: Array<{ day: string; users: number }>;
      anonVisits: Array<{ day: string; sessions: number }>;
      uniqueIps?: Array<{ day: string; visitors: number }>;
      uniqueIpsTotal?: { total: number; authed: number; anon: number };
      topRoutes: Array<{ target: string; hits: number; source?: string }>;
      topAreas: Array<{ target: string; hits: number }>;
      ctaClicks: Array<{ target: string; hits: number }>;
      modalOpens: Array<{ target: string; hits: number }>;
      topCountries?: Array<{ target: string; hits: number }>;
      topRegions?: Array<{ target: string; hits: number }>;
      topSources?: Array<{ target: string; hits: number }>;
      forecastRange?: Array<{ target: string; hits: number }>;
      freeTierAtLimit: Array<{ account_id: string; email: string; active_days: number }>;
    }> {
      return await this.fetchWithAuth(`/admin/telemetry?days=${days}`);
    },

    async fetchUserDetails(userId: string): Promise<{
      user: {
        id: string;
        email: string;
        username?: string;
        image_link?: string;
      };
      roles: Array<{ id: string; name: string }>;
      permissions: Array<{
        id: string;
        resource: string;
        action: string;
        description?: string;
      }>;
    }> {
      return await this.fetchWithAuth(`/admin/users/${userId}`);
    },

    async updateUserRoles(
      userId: string,
      roleIds: string[],
    ): Promise<{
      message: string;
      roles: Array<{ id: string; name: string }>;
    }> {
      return await this.fetchWithAuthPut(`/admin/users/${userId}/roles`, {
        roleIds,
      });
    },

    async createRole(
      name: string,
      permissionIds: string[],
    ): Promise<{
      message: string;
      role: {
        id: string;
        name: string;
        permissions: Array<{
          id: string;
          resource: string;
          action: string;
          description?: string;
        }>;
      };
    }> {
      return await this.fetchWithAuthPost(`/admin/roles`, {
        name,
        permissionIds,
      });
    },

    async updateRolePermissions(
      roleId: string,
      permissionIds: string[],
    ): Promise<{
      message: string;
      role: {
        id: string;
        name: string;
        permissions: Array<{
          id: string;
          resource: string;
          action: string;
          description?: string;
        }>;
      };
    }> {
      return await this.fetchWithAuthPut(`/admin/roles/${roleId}`, {
        permissionIds,
      });
    },

    async deleteRole(roleId: string): Promise<{ message: string }> {
      return await this.fetchWithAuthDelete(`/admin/roles/${roleId}`);
    },

    async deleteUser(userId: string): Promise<{ message: string }> {
      return await this.fetchWithAuthDelete(`/admin/users/${userId}`);
    },

    // Plan management methods
    async fetchAdminPlans(): Promise<{
      plans: Array<{
        id: string;
        name: string;
        description: string | null;
        display_order: number;
        is_hidden: boolean;
        max_active_days_per_month: number | null;
        api_requests_per_month: number | null;
        api_rpm: number | null;
        price_sek_per_month: number | null;
        roles: Array<{ id: string; name: string }>;
        userCount: number;
      }>;
    }> {
      return await this.fetchWithAuth(`/admin/plans`);
    },

    async createPlan(data: {
      name: string;
      description?: string;
      roleIds: string[];
      isHidden?: boolean;
      maxActiveDaysPerMonth?: number | null;
      apiRequestsPerMonth?: number | null;
      apiRpm?: number | null;
      priceSekPerMonth?: number | null;
    }): Promise<{ message: string; plan: any }> {
      return await this.fetchWithAuthPost(`/admin/plans`, data);
    },

    async updatePlan(
      planId: string,
      data: {
        name?: string;
        description?: string;
        roleIds?: string[];
        isHidden?: boolean;
        maxActiveDaysPerMonth?: number | null;
        apiRequestsPerMonth?: number | null;
        apiRpm?: number | null;
      },
    ): Promise<{ message: string; plan: any }> {
      return await this.fetchWithAuthPut(`/admin/plans/${planId}`, data);
    },

    async deletePlan(planId: string): Promise<{ message: string }> {
      return await this.fetchWithAuthDelete(`/admin/plans/${planId}`);
    },

    async assignUserPlan(
      userId: string,
      planId: string | null,
    ): Promise<{ message: string }> {
      return await this.fetchWithAuthPut(`/admin/users/${userId}/plan`, {
        planId,
      });
    },

    // Settings methods
    async fetchAdminSettings(): Promise<{
      settings: Array<{
        key: string;
        value: string;
        description: string | null;
        updated_at: string;
      }>;
    }> {
      return await this.fetchWithAuth(`/admin/settings`);
    },

    async updateAdminSetting(
      key: string,
      value: string,
    ): Promise<{ message: string; setting: any }> {
      return await this.fetchWithAuthPut(`/admin/settings`, { key, value });
    },

    // Helper to handle common response errors
    async handleResponse(res: Response) {
      if (res.status === 401) {
        window.location.href = "/login";
        throw new Error("Unauthorized");
      }

      if (res.status === 402) {
        const data = await res.json().catch(() => ({}));
        // 402 signals the plan active-day cap — pop the upgrade modal.
        if (data.error === "free_tier_limit_reached") {
          window.dispatchEvent(new CustomEvent("free-tier-limit", { detail: data }));
        }
        // Tag the error so callers can recognise the expected 402 condition and
        // skip noisy console logging for it.
        const err = new Error(data.message || "Payment required") as Error & { status?: number; code?: string };
        err.status = 402;
        err.code = data.error;
        throw err;
      }

      if (res.status === 403) {
        // Check if permissions have changed (requires re-login)
        const data = await res.json().catch(() => ({}));
        if (data.error === "PermissionsChanged") {
          // The JWT (in the _auth cookie) is stale after a plan/role change.
          // A page refresh can't fix it — only a fresh /login mints a new token.
          // Latch so all further requests short-circuit (no loop, no AI cost),
          // then let the SessionExpiredNotice modal prompt a user-triggered
          // re-login. We deliberately do NOT auto-redirect or clear localStorage
          // (auth lives in a cookie, so that was a no-op anyway).
          permissionsInvalidated = true;
          window.dispatchEvent(new CustomEvent("permissions-changed"));
          throw new Error("Permissions changed - please log in again");
        }
        throw new Error(data.message || "Forbidden - Permission denied");
      }

      return await res.json();
    },

    async fetchApiKeys(): Promise<{
      apiKeys: Array<{
        id: string;
        name: string;
        keyPrefix: string;
        totalRequests: number;
        lastUsedAt: string | null;
        isActive: boolean;
        createdAt: string;
        ownerUsername: string | null;
        ownerEmail: string | null;
      }>;
    }> {
      return await this.fetchWithAuth(`/admin/api-keys`);
    },

    async toggleApiKey(
      id: string,
      isActive: boolean,
    ): Promise<{
      message: string;
      apiKey: {
        id: string;
        name: string;
        keyPrefix: string;
        totalRequests: number;
        lastUsedAt: string | null;
        isActive: boolean;
        createdAt: string;
      };
    }> {
      return await this.fetchWithAuthPut(`/admin/api-keys/${id}`, { isActive });
    },

    async deleteApiKey(id: string): Promise<{ message: string }> {
      return await this.fetchWithAuthDelete(`/admin/api-keys/${id}`);
    },

    async fetchMyApiKeys(): Promise<{
      apiKeys: Array<{
        id: string;
        name: string;
        keyPrefix: string;
        totalRequests: number;
        lastUsedAt: string | null;
        isActive: boolean;
        createdAt: string;
      }>;
    }> {
      return await this.fetchWithAuth(`/accounts/api-keys`);
    },

    async createMyApiKey(name: string): Promise<{
      apiKey: {
        id: string;
        name: string;
        keyPrefix: string;
        totalRequests: number;
        lastUsedAt: string | null;
        isActive: boolean;
        createdAt: string;
      };
      key: string;
    }> {
      return await this.fetchWithAuthPost(`/accounts/api-keys`, { name });
    },

    async toggleMyApiKey(
      id: string,
      isActive: boolean,
    ): Promise<{
      message: string;
      apiKey: {
        id: string;
        name: string;
        keyPrefix: string;
        totalRequests: number;
        lastUsedAt: string | null;
        isActive: boolean;
        createdAt: string;
      };
    }> {
      return await this.fetchWithAuthPut(`/accounts/api-keys/${id}`, {
        isActive,
      });
    },

    async deleteMyApiKey(id: string): Promise<{ message: string }> {
      return await this.fetchWithAuthDelete(`/accounts/api-keys/${id}`);
    },

    // ── Plan / upgrade requests ─────────────────────────────────────────────

    async fetchMyPlan(): Promise<{
      current: { id: string; name: string; description: string | null; displayOrder: number; stripePriceId: string | null } | null;
      selectable: Array<{ id: string; name: string; description: string | null; displayOrder: number; stripePriceId: string | null }>;
      subscriptionStatus: string | null;
      hasBilling: boolean;
      planLocked: boolean;
      pendingRequest: { id: string; requestedPlanId: string; requestedAt: string } | null;
    }> {
      return await this.fetchWithAuth(`/accounts/me/plan`);
    },

    // Create Stripe Checkout for a paid plan: returns a Stripe-hosted URL to redirect to.
    // The plan only flips once Stripe confirms payment via webhook.
    async createCheckoutSession(planId: string): Promise<{ url: string }> {
      return await this.fetchWithAuthPost(`/billing/checkout`, { planId });
    },

    // Create Stripe Customer Portal session: returns a Stripe-hosted URL where the user can
    // update their card, view invoices, and cancel. flow "subscription_cancel"
    // deep-links straight into the portal's cancel flow (paid -> free downgrade);
    // flow "subscription_update_confirm" (+ planId) deep-links into the portal's
    // confirm screen for switching to another paid plan (paid -> paid, prorated).
    async createPortalSession(
      flow?: "subscription_cancel" | "subscription_update_confirm",
      planId?: string,
    ): Promise<{ url: string }> {
      return await this.fetchWithAuthPost(`/billing/portal`, {
        ...(flow ? { flow } : {}),
        ...(planId ? { planId } : {}),
      });
    },

    // Live-read of the subscription's pending-cancellation state from Stripe;
    // nothing is stored locally, so this is the only source for the
    // "your plan ends on <date>" notice.
    async fetchBillingSubscription(): Promise<{
      cancelAtPeriodEnd: boolean;
      currentPeriodEnd: string | null;
    }> {
      return await this.fetchWithAuth(`/billing/subscription`);
    },

    // Pull the live subscription state from Stripe onto the account and return
    // the resulting status — beats the webhook on the Checkout return page.
    async syncBilling(): Promise<{ subscriptionStatus: string | null }> {
      return await this.fetchWithAuthPost(`/billing/sync`, {});
    },

    async requestPlanChange(
      requestedPlanId: string,
      note?: string,
    ): Promise<{ id: string; requestedAt: string; status: string }> {
      return await this.fetchWithAuthPost(`/accounts/me/plan-change-request`, {
        requestedPlanId,
        note,
      });
    },

    async fetchPlanChangeRequests(status: string = "pending"): Promise<{
      requests: Array<{
        id: string;
        accountId: string;
        email: string;
        username: string | null;
        currentPlanName: string | null;
        requestedPlanId: string;
        requestedPlanName: string;
        status: string;
        note: string | null;
        requestedAt: string;
        decidedAt: string | null;
        decidedBy: string | null;
      }>;
    }> {
      return await this.fetchWithAuth(`/admin/plan-change-requests?status=${status}`);
    },

    async approvePlanChangeRequest(id: string): Promise<{ ok: true }> {
      return await this.fetchWithAuthPost(`/admin/plan-change-requests/${id}/approve`, {});
    },

    async rejectPlanChangeRequest(id: string): Promise<{ ok: true }> {
      return await this.fetchWithAuthPost(`/admin/plan-change-requests/${id}/reject`, {});
    },

    async reorderPlans(planIds: string[]): Promise<{ ok: true }> {
      return await this.fetchWithAuthPost(`/admin/plans/reorder`, { planIds });
    },

    // Shared request path. On an expired token (401) or a stale-permission 403
    // it transparently refreshes the access token once and retries, so a plan/
    // role change is picked up without a forced re-login. Only when the refresh
    // itself fails does it fall through to handleResponse (modal / login).
    async _request(method: string, endpoint: string, body?: unknown) {
      if (permissionsInvalidated) throw new Error("Session expired - please log in again");
      const url = `${apiUrl}/api${endpoint}`;
      const build = (auth: string): RequestInit => ({
        method,
        headers: { Authorization: auth, "Content-Type": "application/json" },
        ...(body !== undefined ? { body: JSON.stringify(body ?? {}) } : {}),
      });

      let res = await fetch(url, build(authHeader()));

      if (res.status === 401 || res.status === 403) {
        let refreshable = res.status === 401;
        if (res.status === 403) {
          const data = await res.clone().json().catch(() => ({}));
          refreshable = data?.error === "PermissionsChanged";
        }
        if (refreshable) {
          const newToken = await refreshSession();
          if (newToken) {
            res = await fetch(url, build(`Bearer ${newToken}`));
          }
        }
      }

      return await this.handleResponse(res);
    },

    async fetchWithAuth(endpoint: string) {
      return await this._request("GET", endpoint);
    },

    async fetchWithAuthPost(endpoint: string, body: unknown) {
      return await this._request("POST", endpoint, body);
    },

    async fetchWithAuthPut(endpoint: string, body: unknown) {
      return await this._request("PUT", endpoint, body);
    },

    async fetchWithAuthDelete(endpoint: string) {
      return await this._request("DELETE", endpoint);
    },
  };
};
